"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function HeaderMenuConsumer({
  mobileMenuOpen,
  setMobileMenuOpen
}: {
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (v: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="205972" className="flex gap-4 items-center">
        <div className="h-4 w-16 bg-muted animate-pulse rounded-sm" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-sm" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-sm" />
      </div>;
  }
  return <>
      {}
      <div className="hidden md:block">
        <NavigationMenu viewport={false}>
          <NavigationMenuList className="gap-8">
            {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
                <NavigationMenuLink asChild>
                  <Link to={item.href || "#"} className="group relative py-2 text-sm font-semibold font-default text-foreground transition-colors hover:text-accent">
                    {item.label}
                    <span className="absolute inset-x-0 bottom-0 h-[2px] origin-center scale-x-0 bg-primary transition-transform duration-300 ease-out group-hover:scale-x-100" />
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>)}
          </NavigationMenuList>
        </NavigationMenu>
      </div>

      {}
      <Button variant="ghost" size="icon" className="md:hidden text-foreground" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
        {mobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
      </Button>

      {}
      {mobileMenuOpen && <div className="absolute top-full left-0 w-full bg-background/95 backdrop-blur-xl border-b border-border shadow-lg md:hidden flex flex-col py-6 px-4 gap-6 z-50">
          <div className="flex flex-col gap-4">
            {menuItems.map((item, i) => <div key={item.id} data-index={i}>
                <Link to={item.href || "#"} className="block text-lg font-semibold font-default text-foreground hover:text-accent transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  {item.label}
                </Link>
              </div>)}
          </div>
          <div className="pt-6 border-t border-border">
            <Button className="w-full bg-primary text-primary-foreground shadow-md shadow-primary/20 relative overflow-hidden group rounded-sm">
              <span className="relative z-10 font-semibold font-default">Get Started</span>
              <span className="absolute inset-0 bg-white/10 animate-pulse opacity-50" />
            </Button>
          </div>
        </div>}
    </>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsScrolled(currentScrollY > 10);
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollHeight > 0) {
        const progress = currentScrollY / scrollHeight * 100;
        setScrollProgress(progress);
      } else {
        setScrollProgress(0);
      }
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 ${isScrolled ? "bg-background/80 backdrop-blur-xl border-b border-border shadow-sm" : "bg-background border-b border-border"}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between relative">
        {}
        <div className="flex-shrink-0 z-10 flex items-center">
          <Link to="/HomePage#" className="flex items-center">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1633756/img/web_designs_by_helen_logo.png" alt="Zemio" className="h-7 w-auto object-contain" />
          </Link>
        </div>

        {}
        <MenuProvider menu_id="44" className="flex-1 flex justify-end md:justify-center items-center">
          <HeaderMenuConsumer mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
        </MenuProvider>

        {}
        <div className="hidden md:flex flex-shrink-0 z-10 items-center ml-8">
          <Button className="bg-primary text-primary-foreground shadow-lg shadow-primary/20 hover:shadow-primary/40 hover:bg-primary/90 transition-all duration-300 relative overflow-hidden group px-6 rounded-sm">
            <span className="relative z-10 font-semibold font-default tracking-normal">Get Started</span>
            <span className="absolute inset-0 bg-white/10 animate-pulse opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </Button>
        </div>
      </div>

      {}
      <div className="absolute bottom-0 left-0 w-full h-[2px] z-20">
        <Progress value={scrollProgress} className="h-full rounded-none bg-transparent [&>div]:bg-primary" />
      </div>
    </section>;
}